/**
 * Сохранённые фильтры списка обращений: критерии, подписи, сборка payload для API.
 */
import type { ProjectRow, SavedFilterCriteria, SavedFilterRow, WorkspaceUserRow } from '@/types/api'
import { statusLabel } from '@/lib/ticket-display'

export const SAVED_FILTER_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'new', label: 'Новое' },
  { value: 'open', label: 'Открыто' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'pending', label: 'Ожидание' },
  { value: 'emergency', label: 'Авария' },
  { value: 'resolved', label: 'Решено' },
  { value: 'closed', label: 'Закрыто' },
]

export const SAVED_FILTER_PRIORITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'low', label: 'Низкий' },
  { value: 'normal', label: 'Обычный' },
  { value: 'high', label: 'Высокий' },
  { value: 'urgent', label: 'Срочный' },
]

export function savedFilterWorkspaceUserLabel(u: WorkspaceUserRow): string {
  const n = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
  return n || u.email || u.id
}

export function savedFilterStatusLabel(v: string): string {
  return SAVED_FILTER_STATUS_OPTIONS.find((o) => o.value === v)?.label ?? statusLabel(v)
}

export function savedFilterPriorityLabel(v: string): string {
  return SAVED_FILTER_PRIORITY_OPTIONS.find((o) => o.value === v)?.label ?? v
}

export function savedFilterNormalizeIdList(v: unknown): string[] {
  if (v == null || v === '') return []
  if (Array.isArray(v)) return v.map(String).filter(Boolean)
  return [String(v)]
}

export function savedFilterCriteriaSummaryLines(
  c: SavedFilterCriteria,
  projects: ProjectRow[],
  users: WorkspaceUserRow[],
): string[] {
  const lines: string[] = []
  const st = c.status
  if (Array.isArray(st) && st.length) {
    lines.push(`Статус: ${st.map(savedFilterStatusLabel).join(', ')}`)
  }
  const pr = c.priority
  if (Array.isArray(pr) && pr.length) {
    lines.push(`Приоритет: ${pr.map(savedFilterPriorityLabel).join(', ')}`)
  }
  const pids = savedFilterNormalizeIdList(c.project_id)
  if (pids.length) {
    const names = pids
      .map((id) => projects.find((p) => p.id === id)?.name || id)
      .filter(Boolean)
    if (names.length) {
      lines.push(`Проект${names.length > 1 ? 'ы' : ''}: ${names.join(', ')}`)
    }
  }
  const aids = savedFilterNormalizeIdList(c.assigned_to)
  if (aids.length) {
    const names = aids
      .map((id) => users.find((u) => u.id === id))
      .map((u) => (u ? savedFilterWorkspaceUserLabel(u) : null))
      .filter(Boolean) as string[]
    if (names.length) lines.push(`Назначено: ${names.join(', ')}`)
  }
  if (c.unassigned === true) lines.push('Не назначено')
  if (typeof c.created_within_hours === 'number' && c.created_within_hours >= 1) {
    lines.push(`Создано за последние ${c.created_within_hours} ч.`)
  }
  if (typeof c.updated_within_hours === 'number' && c.updated_within_hours >= 1) {
    lines.push(`Обновлено за последние ${c.updated_within_hours} ч.`)
  }
  if (c.has_customer_reply === true) lines.push('Есть ответ клиента')
  return lines
}

export type SavedFilterFormState = {
  name: string
  description: string
  color: string
  is_favorite: boolean
  available_to_all: boolean
  statuses: string[]
  priorities: string[]
  projectIds: string[]
  assignedUserIds: string[]
  unassigned: boolean
  created_within_hours: string
  updated_within_hours: string
}

export function emptySavedFilterForm(): SavedFilterFormState {
  return {
    name: '',
    description: '',
    color: '#3B82F6',
    is_favorite: false,
    available_to_all: false,
    statuses: [],
    priorities: [],
    projectIds: [],
    assignedUserIds: [],
    unassigned: false,
    created_within_hours: '',
    updated_within_hours: '',
  }
}

export function savedFilterFormFromRow(f: SavedFilterRow): SavedFilterFormState {
  const c = f.criteria
  return {
    name: f.name,
    description: f.description ?? '',
    color: f.color || '#3B82F6',
    is_favorite: f.is_favorite,
    available_to_all: f.available_to_all === true,
    statuses: Array.isArray(c.status) ? [...c.status] : [],
    priorities: Array.isArray(c.priority) ? [...c.priority] : [],
    projectIds: savedFilterNormalizeIdList(c.project_id),
    assignedUserIds: savedFilterNormalizeIdList(c.assigned_to),
    unassigned: c.unassigned === true,
    created_within_hours:
      typeof c.created_within_hours === 'number' ? String(c.created_within_hours) : '',
    updated_within_hours:
      typeof c.updated_within_hours === 'number' ? String(c.updated_within_hours) : '',
  }
}

export function savedFilterCriteriaFromForm(s: SavedFilterFormState): SavedFilterCriteria {
  const criteria: SavedFilterCriteria = {}
  if (s.statuses.length) criteria.status = [...s.statuses]
  if (s.priorities.length) criteria.priority = [...s.priorities]
  if (s.projectIds.length === 1) criteria.project_id = s.projectIds[0]
  else if (s.projectIds.length > 1) criteria.project_id = [...s.projectIds]
  if (s.assignedUserIds.length === 1) criteria.assigned_to = s.assignedUserIds[0]
  else if (s.assignedUserIds.length > 1) criteria.assigned_to = [...s.assignedUserIds]
  if (s.unassigned) criteria.unassigned = true
  const cwh = parseInt(s.created_within_hours, 10)
  if (!Number.isNaN(cwh) && cwh >= 1) criteria.created_within_hours = cwh
  const uwh = parseInt(s.updated_within_hours, 10)
  if (!Number.isNaN(uwh) && uwh >= 1) criteria.updated_within_hours = uwh
  return criteria
}

/** Поиск по названию, описанию и тексту условий */
export function savedFilterMatchesQuery(
  f: SavedFilterRow,
  q: string,
  projects: ProjectRow[],
  users: WorkspaceUserRow[],
): boolean {
  const s = q.trim().toLowerCase()
  if (!s) return true
  if (f.name.toLowerCase().includes(s)) return true
  if ((f.description || '').toLowerCase().includes(s)) return true
  const lines = savedFilterCriteriaSummaryLines(f.criteria, projects, users)
  return lines.some((line) => line.toLowerCase().includes(s))
}
